import React, { createContext, useContext, useState, useEffect } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { lightTheme, darkTheme } from '../theme/themes';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Get initial theme from localStorage or default to light
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('masterski-theme');
    return saved === 'dark';
  });

  const currentTheme = isDark ? darkTheme : lightTheme;

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('masterski-theme', isDark ? 'dark' : 'light');

    // Update body background
    document.body.style.backgroundColor = currentTheme.bgSecondary;
    document.body.style.color = currentTheme.textPrimary;
  }, [isDark, currentTheme]);

  const toggleTheme = () => {
    setIsDark(prev => !prev);
  };

  const value = {
    isDark,
    theme: currentTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        theme={{
          algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
          token: currentTheme.token,
          components: {
            Layout: {
              bodyBg: currentTheme.bgSecondary,
              headerBg: currentTheme.bgPrimary,
              siderBg: currentTheme.sidebarBg,
            },
            Menu: {
              darkItemBg: currentTheme.sidebarBg,
              darkSubMenuItemBg: currentTheme.sidebarBg,
              darkItemSelectedBg: currentTheme.sidebarActive,
            },
            Table: {
              headerBg: currentTheme.tableHeaderBg,
              rowHoverBg: currentTheme.tableRowHover,
            },
            Card: {
              colorBgContainer: currentTheme.bgCard,
            },
          },
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
